import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import { Activity, Percent, CalendarDays, Target, Filter } from "lucide-react";
import StatCard from "@/components/StatCard";
import { OMFS_CATEGORIES } from "@/lib/omfsCategories";

const COLORS = ["#7CB342", "#558B2F", "#9CCC65", "#8896A0", "#B5BFC6", "#33691E"];

function Section({ title, subtitle, children, testId }) {
  return (
    <div className="neu-card p-6 md:p-7" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="heading text-lg text-[#3F4E5B]">{title}</div>
          {subtitle && <div className="text-xs subtle mt-1">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [omfsFilter, setOmfsFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = omfsFilter ? { omfs_category: omfsFilter } : {};
    api
      .get("/analytics", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [omfsFilter]);

  if (loading && !data) {
    return (
      <Layout>
        <div className="neu-card p-10 subtle text-center">Loading analytics…</div>
      </Layout>
    );
  }

  if (!data) return null;

  const {
    total_cases, primary_surgeon_pct, avg_cases_per_month,
    monthly_trends, role_distribution, category_breakdown,
    omfs_category_breakdown, top_procedures, top_attendings, board,
  } = data;

  return (
    <Layout>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="heading text-3xl md:text-4xl text-[#3F4E5B]">Analytics</h1>
          <p className="subtle mt-2">
            {omfsFilter
              ? `Filtered to ${omfsFilter}`
              : "Your surgical performance at a glance."}
          </p>
        </div>
        <div className="neu-card px-4 py-3 flex items-center gap-3">
          <Filter className="w-4 h-4 text-neu-accent" />
          <label className="text-xs uppercase tracking-wider subtle" htmlFor="a-omfs-filter">
            OMFS category
          </label>
          <select
            id="a-omfs-filter"
            value={omfsFilter}
            onChange={(e) => setOmfsFilter(e.target.value)}
            className="neu-input py-2 pl-3 pr-8 min-w-[16rem]"
            data-testid="analytics-omfs-filter"
          >
            <option value="">All categories</option>
            {OMFS_CATEGORIES.map((c) => (
              <option key={c} value={c} data-testid={`analytics-omfs-filter-opt-${c}`}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <StatCard testId="a-total" icon={Activity} label="Total cases" value={total_cases} accent />
        <StatCard testId="a-primary" icon={Percent} label="Primary %" value={`${primary_surgeon_pct}%`} />
        <StatCard testId="a-avg" icon={CalendarDays} label="Avg / month" value={avg_cases_per_month} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2 mt-8">
        <Section title="Cases per month" subtitle="Last 12 months by role" testId="chart-monthly">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthly_trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#E4EBF1", border: "none", borderRadius: 12 }} />
              <Legend />
              <Bar dataKey="primary" name="Primary" stackId="a" fill="#7CB342" radius={[4, 4, 0, 0]} />
              <Bar dataKey="first_assist" name="1st Assist" stackId="a" fill="#9CCC65" />
              <Bar dataKey="other" name="Other" stackId="a" fill="#B5BFC6" radius={[0, 0, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        <Section title="Role distribution" testId="chart-role">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={role_distribution} dataKey="value" nameKey="name" outerRadius={100} label>
                {role_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: "#E4EBF1", border: "none", borderRadius: 12 }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Section>

        <Section title="Procedure categories" testId="chart-category">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={category_breakdown} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={140} />
              <Tooltip contentStyle={{ backgroundColor: "#E4EBF1", border: "none", borderRadius: 12 }} />
              <Bar dataKey="value" fill="#7CB342" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        <Section title="OMFS categories" subtitle="Cases by OMFS category" testId="chart-omfs-category">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={omfs_category_breakdown || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={180} />
              <Tooltip contentStyle={{ backgroundColor: "#E4EBF1", border: "none", borderRadius: 12 }} />
              <Bar dataKey="value" fill="#558B2F" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        <Section title="Top procedures" subtitle="Most performed" testId="top-procedures">
          <div className="space-y-3">
            {top_procedures.length === 0 && <div className="subtle text-sm">No data yet</div>}
            {top_procedures.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-neu-bg shadow-neu-inset-sm flex items-center justify-center text-xs font-bold text-neu-accent">
                  {i + 1}
                </div>
                <div className="flex-1 truncate">{p.name}</div>
                <div className="font-semibold">{p.value}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 mt-6">
        <Section title="Top attendings" subtitle="Cases per attending" testId="top-attendings">
          <div className="space-y-3">
            {top_attendings.length === 0 && <div className="subtle text-sm">No data yet</div>}
            {top_attendings.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-neu-bg shadow-neu-inset-sm flex items-center justify-center text-sm font-bold text-neu-accent">
                  {p.name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </div>
                <div className="flex-1 truncate">{p.name}</div>
                <div className="font-semibold">{p.value} cases</div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Board prep" subtitle={`Estimated completion: ${board.eta}`} testId="board-analytics">
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-5 h-5 text-neu-accent" />
            <div>
              <div className="heading text-2xl text-[#3F4E5B]">
                {board.current} / {board.target}
              </div>
              <div className="text-xs subtle">Cases toward board target</div>
            </div>
          </div>
          <div className="h-4 rounded-full bg-neu-bg shadow-neu-inset-sm overflow-hidden mb-4">
            <div
              className="h-full bg-gradient-to-r from-neu-accent to-[#AED581]"
              style={{ width: `${Math.min(board.progress_pct, 100)}%` }}
            />
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="neu-inset p-3">
              <div className="text-xs subtle">Progress</div>
              <div className="heading text-lg text-neu-accent">{board.progress_pct}%</div>
            </div>
            <div className="neu-inset p-3">
              <div className="text-xs subtle">As primary</div>
              <div className="heading text-lg">{board.primary_count}</div>
            </div>
            <div className="neu-inset p-3">
              <div className="text-xs subtle">Remaining</div>
              <div className="heading text-lg">{Math.max(board.target - board.current, 0)}</div>
            </div>
          </div>
        </Section>
      </div>

      <div className="neu-card p-6 md:p-7 mt-6" data-testid="monthly-summary">
        <div className="heading text-lg text-[#3F4E5B] mb-4">Monthly summary</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left subtle text-xs uppercase tracking-wider border-b border-neu-muted/20">
                <th className="py-3 pr-4">Month</th>
                <th className="py-3 pr-4">Cases</th>
                <th className="py-3 pr-4">Primary</th>
                <th className="py-3 pr-4">1st Assist</th>
              </tr>
            </thead>
            <tbody>
              {monthly_trends.map((m) => (
                <tr key={m.month} className="border-b border-neu-muted/10 last:border-b-0">
                  <td className="py-2 pr-4 whitespace-nowrap">{m.month}</td>
                  <td className="py-2 pr-4 font-semibold">{m.total}</td>
                  <td className="py-2 pr-4">{m.primary}</td>
                  <td className="py-2 pr-4">{m.first_assist}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
